import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    precheckUrl: String
  }

  static targets = ["checkboxAll", "checkbox", "exportButton", "submitButton", "checkboxText"]

  connect() {
    if (this.hasCheckboxAllTarget) {
      this.selected = new Set()
      this.abortController = new AbortController()
    }
  }

  disconnect() {
    if (this.hasCheckboxAllTarget) {
      this.abort()
    }
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.selected.clear()
    this.checkboxTargets.forEach(cb => {
      cb.checked = checked
      if (checked) {
        this.selected.add(cb.dataset.reportType)
      }
    })
    this.exportButtonTarget.disabled = !checked
    this.updateText()
  }

  updateText() {
    this.checkboxTextTarget.value = Array.from(this.selected).join(", ")
  }

  toggle(e) {
    if (e.target.checked) {
      this.selected.add(e.target.dataset.reportType)
    } else {
      this.selected.delete(e.target.dataset.reportType)
      if (this.hasCheckboxAllTarget) this.checkboxAllTarget.checked = false
    }
    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.disabled = (this.selected.size === 0)
    }
    this.updateText()
  }

  async submit(e) {
    e.preventDefault();
    try {
      this.submitButtonTarget.disabled = true
      this.exportButtonTarget.disabled = true
      const form = this.element
      const url = form.action

      const formData = new FormData(form)

      await this.runPrecheck(formData)

      const tokenMeta = document.querySelector("meta[name=csrf-token]");
      const csrfToken = tokenMeta ? tokenMeta.content : "";
      
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken
        },
        body: formData,
        signal: this.abortController.signal
      })
      
      if (!response.ok) throw new Error("Cannot export file")

      const disposition = response.headers.get("Content-Disposition")
      const filename = this.extractFilename(disposition, formData)

      const blob = await response.blob()
      const urlBlob = window.URL.createObjectURL(blob)

      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = filename
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlBlob);
    } catch(err) {
      if (err?.name === "AbortError" || err?.message === "EXPORT_PRECHECK_CANCELLED") {
        return
      }
      console.error("Download failed:", err)
      alert("Failed to export report")
    } finally {
      this.submitButtonTarget.disabled = false
      this.exportButtonTarget.disabled = false
    }
  }

  abort() {
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  csrfToken() {
    return document.querySelector("meta[name=csrf-token]")?.content || ""
  }

  extractFilename(disposition, formData) {
    if (!disposition) return `report.${formData.get('export_report_form[file_type]')}`
    const parts = disposition.split(";").map(p => p.trim())
    const filename = parts.find(p => p.toLowerCase().startsWith("filename="))
    if (filename) {
      return filename.split("=")[1].replace(/^"|"$/g, "")
    }
    return `report.${formData.get('export_report_form[file_type]')}`
  }

  async runPrecheck(formData) {
    if (!this.hasPrecheckUrlValue) return
    if (formData.get("export_report_form[heatmap]") !== "1") return

    const precheckData = new FormData()
    for (const [key, value] of formData.entries()) {
      if (!key.startsWith("export_report_form[")) continue
      precheckData.append(key, value)
    }

    let response
    try {
      response = await fetch(this.precheckUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken()
        },
        body: precheckData,
        signal: this.abortController.signal
      })
    } catch (err) {
      console.error("Precheck request failed:", err)
      return
    }

    if (!response.ok) return
    let payload = {}
    try {
      payload = await response.json()
    } catch {
      return
    }

    const warnings = Array.isArray(payload.warnings) ? payload.warnings : []
    if (warnings.length === 0) return

    const details = warnings
      .slice(0, 8)
      .map((w) => `- ${w.floor_plan_name} / ${w.band}: ${w.reason}`)
      .join("\n")
    const truncated = warnings.length > 8 ? `\n- ...and ${warnings.length - 8} more` : ""
    const message = `Heatmap export warning:\n${details}${truncated}\n\nExport will continue and unavailable heatmaps will be replaced with text notes.\n\nContinue?`
    const proceed = window.confirm(message)
    if (!proceed) {
      throw new Error("EXPORT_PRECHECK_CANCELLED")
    }
  }
}
